import { create } from 'zustand';
import { api } from '@/api/endpoints';
import { evaluateUpdate, type UpdateSeverity } from '@/lib/version';
import { StorageKeys, VersionCheckConfig } from '@/lib/config';
import { applySwUpdate, registerForUpdates } from '@/services/swUpdate';
import { createLogger } from '@/lib/logger';

const log = createLogger('update');

const RUNNING: string =
  typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';

interface UpdateState {
  running: string;
  current: string | null;
  minimum: string | null;
  serverVersion: string | null;
  severity: UpdateSeverity;
  /** A new build was detected by the service worker (assets ready to swap). */
  swWaiting: boolean;
  /** Driver dismissed the optional prompt for the current advertised version. */
  dismissed: boolean;
  applying: boolean;

  check: () => Promise<void>;
  start: () => void;
  dismiss: () => void;
  /** Returns false when the new build is not downloaded yet (retry shortly). */
  apply: () => Promise<boolean>;
}

let timer: ReturnType<typeof setInterval> | null = null;
let started = false;

export const useUpdateStore = create<UpdateState>((set, get) => ({
  running: RUNNING,
  current: null,
  minimum: null,
  serverVersion: null,
  severity: 'ok',
  swWaiting: false,
  dismissed: false,
  applying: false,

  check: async () => {
    try {
      const v = await api.appVersion();
      const severity = evaluateUpdate(RUNNING, v.current, v.minimum);
      const dismissedVersion = localStorage.getItem(StorageKeys.updateDismissedVersion);
      const dismissed = severity === 'optional' && dismissedVersion === v.current;
      set({
        current: v.current,
        minimum: v.minimum,
        serverVersion: v.serverVersion ?? null,
        severity,
        dismissed,
      });
    } catch (e) {
      log.debug('version check failed', e);
    }
  },

  start: () => {
    if (started) return;
    started = true;
    void get().check();
    void registerForUpdates(() => set({ swWaiting: true }));
    if (timer) clearInterval(timer);
    timer = setInterval(() => void get().check(), VersionCheckConfig.pollMs);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void get().check();
      });
    }
  },

  dismiss: () => {
    const cur = get().current;
    if (cur) localStorage.setItem(StorageKeys.updateDismissedVersion, cur);
    set({ dismissed: true });
  },

  apply: async () => {
    set({ applying: true });
    const result = await applySwUpdate();
    if (result === 'not-ready') {
      // New build still downloading in the background; unlock the button so
      // the driver can retry once it lands (the banner stays up).
      set({ applying: false });
      return false;
    }
    return true;
  },
}));
